import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress, Modal, Typography, Button } from '@mui/material';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, userStatus } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user is unverified (just registered, no docs yet), show modal and redirect to document upload
  if (userStatus === 'unverified') {
    return (
      <>
        <Modal
          open={true}
          aria-labelledby="verification-modal-title"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="warning.main">
              Complete Your Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
              You need to submit your documents for verification before accessing the dashboard.
              Please upload your KYC documents to continue.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => {
                window.location.href = '/document-upload';
              }}
              sx={{ mb: 1 }}
            >
              Upload Documents
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                localStorage.clear();
                window.location.href = '/login';
              }}
            >
              Logout
            </Button>
          </Box>
        </Modal>
        <Box sx={{ filter: 'blur(5px)', pointerEvents: 'none' }}>
          {children}
        </Box>
      </>
    );
  }

  // If user is pending (docs submitted, awaiting admin approval)
  if (userStatus === 'pending') {
    return (
      <>
        <Modal
          open={true}
          aria-labelledby="pending-modal-title"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold" color="info.main">
              Verification Pending
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 2 }}>
              Your documents are under review. Our team will get in touch with you shortly to explain the payment process and further steps to complete your registration.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => {
                window.location.href = '/verification-pending';
              }}
            >
              Check Status
            </Button>
          </Box>
        </Modal>
        <Box sx={{ filter: 'blur(5px)', pointerEvents: 'none' }}>
          {children}
        </Box>
      </>
    );
  }

  // User is verified - allow access
  return children;
};

export default ProtectedRoute;